import React from "react";
import { useLanguage } from "../../i18n";
import { FURNISHING_ITEM_KEYS, type CustomItemDraft } from "../../furnishingItems";

export type { CustomItemDraft } from "../../furnishingItems";

const OTHER_VALUE = "__other__";

// A native <select>'s closed state can be skinned to match the app (see
// .input-wrap), but its open option list is rendered by the OS/browser and
// can't be restyled at all — hence this custom trigger + popover instead,
// built from the same .menu-group/.menu-item rows already used for other
// in-app option lists (see Settings).
function ItemPickerDropdown({
  placeholder,
  options,
  accentOption,
  onPick,
}: {
  placeholder: string;
  options: { value: string; label: string }[];
  accentOption: { value: string; label: string };
  onPick: (value: string) => void;
}) {
  const [open, setOpen] = React.useState(false);
  const rootRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (!open) return;
    function onDocPointerDown(e: MouseEvent) {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    }
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onDocPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onDocPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  function pick(value: string) {
    setOpen(false);
    onPick(value);
  }

  return (
    <div ref={rootRef} style={{ position: "relative" }}>
      <button
        type="button"
        className="input-wrap select-trigger"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <span className="placeholder">{placeholder}</span>
        <svg className={`select-chevron${open ? " open" : ""}`} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4">
          <path d="M6 9l6 6 6-6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      {open && (
        <div className="select-popover menu-group" role="listbox" style={{ marginTop: 0 }}>
          {options.map((o) => (
            <div key={o.value} className="menu-item" role="option" aria-selected={false} onClick={() => pick(o.value)}>
              {o.label}
            </div>
          ))}
          <div className="menu-item accent" role="option" aria-selected={false} onClick={() => pick(accentOption.value)}>
            {accentOption.label}
          </div>
        </div>
      )}
    </div>
  );
}

// Free-text furniture/appliance rows alongside the fixed preset list — for
// anything an owner has that isn't one of the preset options. Just the list
// here; adding a new row is triggered from FurnishingItemsEditor's single
// dropdown below (its "Other" option), not a separate button, so there's
// only one "add an item" control on screen.
function CustomFurnishingItemsList({
  items,
  onChange,
}: {
  items: CustomItemDraft[];
  onChange: (items: CustomItemDraft[]) => void;
}) {
  const { t } = useLanguage();

  function update(i: number, patch: Partial<CustomItemDraft>) {
    onChange(items.map((it, idx) => (idx === i ? { ...it, ...patch } : it)));
  }
  function remove(i: number) {
    onChange(items.filter((_, idx) => idx !== i));
  }

  return (
    <>
      {items.map((it, i) => (
        <div key={i} style={{ display: "flex", gap: 8, marginBottom: 8, alignItems: "flex-end" }}>
          <div className="field" style={{ flex: 2, marginBottom: 0 }}>
            <div className="input-wrap">
              <input
                value={it.name}
                onChange={(e) => update(i, { name: e.target.value })}
                placeholder={t("editPropertyForm.customItemNamePlaceholder")}
                autoFocus={!it.name}
              />
            </div>
          </div>
          <div className="field" style={{ flex: 1, marginBottom: 0 }}>
            <div className="input-wrap">
              <input type="number" min={0} value={it.qty} onChange={(e) => update(i, { qty: e.target.value })} placeholder="0" />
            </div>
          </div>
          <button type="button" className="link-btn" style={{ color: "var(--danger)", flexShrink: 0 }} onClick={() => remove(i)}>
            {t("common.remove")}
          </button>
        </div>
      ))}
    </>
  );
}

// The full furnishing-items section: the fixed preset list (each item
// individually removable) plus free-text custom rows — both added through
// the same dropdown below (presets by name, or its "Other" option for a
// custom row), so there's a single, obvious "add an item" control.
export function FurnishingItemsEditor({
  presetItems,
  onPresetChange,
  removedPresets,
  onRemovedPresetsChange,
  customItems,
  onCustomItemsChange,
}: {
  presetItems: Record<string, string>;
  onPresetChange: (key: string, value: string) => void;
  removedPresets: string[];
  onRemovedPresetsChange: (keys: string[]) => void;
  customItems: CustomItemDraft[];
  onCustomItemsChange: (items: CustomItemDraft[]) => void;
}) {
  const { t } = useLanguage();
  // removedPresets doubles as "not yet added" here — callers start every key
  // in it (or every key with no saved quantity) so the list opens empty
  // instead of showing all 9 preset fields at once, and an item only
  // reappears in the picker once its "Remove" is clicked.
  const visible = FURNISHING_ITEM_KEYS.filter((key) => !removedPresets.includes(key));
  const hidden = FURNISHING_ITEM_KEYS.filter((key) => removedPresets.includes(key));

  return (
    <>
      {visible.map((key) => (
        <div key={key} style={{ display: "flex", gap: 8, marginBottom: 8, alignItems: "center" }}>
          <div className="sub" style={{ flex: 2 }}>{t(`editPropertyForm.item_${key}`)}</div>
          <div className="field" style={{ flex: 1, marginBottom: 0 }}>
            <div className="input-wrap">
              <input type="number" min={0} value={presetItems[key] ?? ""} onChange={(e) => onPresetChange(key, e.target.value)} placeholder="0" />
            </div>
          </div>
          <button
            type="button"
            className="link-btn"
            style={{ color: "var(--danger)", flexShrink: 0 }}
            onClick={() => {
              onRemovedPresetsChange([...removedPresets, key]);
              onPresetChange(key, "");
            }}
          >
            {t("common.remove")}
          </button>
        </div>
      ))}

      <CustomFurnishingItemsList items={customItems} onChange={onCustomItemsChange} />

      <div className="field" style={{ marginTop: visible.length > 0 || customItems.length > 0 ? 10 : 0, marginBottom: 0 }}>
        <ItemPickerDropdown
          placeholder={t("editPropertyForm.addFurnitureItemPlaceholder")}
          options={hidden.map((key) => ({ value: key, label: t(`editPropertyForm.item_${key}`) }))}
          accentOption={{ value: OTHER_VALUE, label: t("editPropertyForm.addCustomItemBtn") }}
          onPick={(val) => {
            if (val === OTHER_VALUE) {
              onCustomItemsChange([...customItems, { name: "", qty: "" }]);
            } else {
              onRemovedPresetsChange(removedPresets.filter((k) => k !== val));
            }
          }}
        />
      </div>
    </>
  );
}
